/**
 * Auth & permissions contracts shared between server and clients.
 *
 * Two classes of permissions:
 *  - GlobalPermission: scoped to the whole installation (user/admin actions).
 *  - ProjectPermission: granted per (user, project) pair.
 */
import { Schema } from "effect";

import { IsoDateTime, ManagedAgentId, ProjectId, TeamId, UserId } from "./baseSchemas";
import { ProviderKind } from "./orchestration";

export const GLOBAL_PERMISSIONS = [
  "u.view",
  "u.create",
  "u.edit",
  "u.delete",
  "u.assign_permissions",
  "u.assign_project",
  "p.create",
  // Workspace terminals (global terminal sessions not tied to a project).
  "p.workspace_terminal",
  // Set/rotate the GitHub PAT used to clone & push for any project.
  "p.project.set_repo_credentials",
  // Configure the Plane connection (installation-wide) and link a project to a
  // Plane board. Grouped with the repo-credential permission rather than split
  // in two: both answer "may this person point our projects at an external
  // system and hold the token that reaches it".
  "p.project.set_plane_credentials",
  // Managed agents (Settings → Agents). `a.view` opens the admin screen and is
  // what lifts team-scoped visibility in the picker; `a.edit` covers create,
  // update and delete. Split because reviewing how an agent is configured —
  // its instructions, its tool allowlist — is a reasonable thing to grant
  // without also granting the ability to change what every thread runs.
  "a.view",
  "a.edit",
  // Agent memories (Settings → Memoria). Same split and same reasoning as the
  // agent pair above: `m.view` opens the screen, `m.edit` covers writing,
  // approving and archiving. The split matters more here than for agents,
  // because approving is where a wrong lesson becomes a standing instruction
  // applied to every future thread — reading the catalogue to understand why an
  // agent behaved a certain way is a much smaller act.
  "m.view",
  "m.edit",
  // Tags (Settings → Etiquetas). The vocabulary is installation-wide and is
  // read by things a person may not otherwise administer — a tag decides which
  // memories an agent loads and which command templates a project is offered —
  // so it gets its own pair rather than riding on `a.*` or `m.*`. Same split
  // and same reasoning as those two: `t.view` opens the screen, `t.edit` covers
  // creating, renaming and archiving. Assigning a tag to something is gated by
  // that thing's own permission, not by these — attaching `dominio:android` to
  // a memory is editing the memory.
  "t.view",
  "t.edit",
  // Publishing a project's local command as a *template*, offered as a starting
  // point in every other project's "Añadir comando".
  //
  // Global rather than project-scoped, and that is the whole point: the act
  // reaches outside the project the command lives in. `p.local.manage` says
  // "you decide what may run on a laptop for this project"; it must not also
  // say "and what everyone else is shown when they declare theirs". The catalog
  // itself stays untouchable from here — a template is copied into a new entry
  // with a new id, so this grant publishes a shape, never a command.
  //
  // Marking one requires this *and* `p.local.manage` on the project the command
  // belongs to: you cannot publish out of a catalog you do not administer.
  "p.local.template",
  // Token/cost accounting (Settings → Usage). Read-only and installation-wide:
  // the aggregates cross every project and every user, so there is no
  // project-scoped version of this that would still answer "what did we spend".
  "usage.view",
  // The provider account's *plan* quota — the rolling 5-hour and weekly windows
  // a Claude.ai or ChatGPT subscription meters — shown inside the composer's
  // context-window popover.
  //
  // Separate from `usage.view` even though both read "how much have we spent",
  // because they answer to different people. `usage.view` opens a settings
  // screen with per-project, per-user token and cost aggregates: an accounting
  // question, asked occasionally. This one puts a number in front of somebody
  // on every single turn, and the number is about the *shared* account: how
  // close the whole installation is to being cut off. That is operational
  // information for whoever has to decide "stop, we're burning the weekly
  // budget" — and noise, or worse an invitation to ration themselves, for
  // everyone else. Granting `usage.view` must not silently do this too.
  //
  // Global rather than project-scoped because the quota is: one Claude account
  // and one Codex account serve every project, so there is no per-project
  // answer to give. UI only — the windows ride inside runtime events the thread
  // already receives, so withholding it hides the block without hiding the
  // data. Migration 051 seeds it to `u.assign_permissions` holders and nobody
  // else, so an admin has it out of the box and hands it further out on
  // purpose. Never treat it as a security boundary.
  "limits.view",
  // Settings → Models and Settings → Advanced.
  //
  // These two are *screen visibility*, not capability, and they are the only
  // permissions in this catalogue that are: what those panels mostly hold are
  // per-browser preferences (custom model slugs, CLI binary overrides, theme-
  // adjacent knobs) that no server endpoint guards because there is no server
  // state to guard. They exist so an installation can keep operator-facing
  // machinery out of a non-technical user's way, not to stop anyone doing
  // something they otherwise could.
  //
  // The blocks inside those panels that *do* reach the server keep their own
  // gates and are unaffected by these: Generation defaults and Side chat still
  // require `a.view`, Providers still requires `p.workspace_terminal`. Never
  // treat `settings.*.view` as a security boundary.
  "settings.models.view",
  "settings.advanced.view",
  // The thread directory (`/threads`): every root thread in the installation,
  // paged and filterable, and the only way to reach somebody else's work.
  //
  // Deliberately not `u.view`. That grant administers *accounts* — who exists,
  // what they may do — and being handed everybody's conversations along with it
  // is a different thing entirely, one a project manager needs without any of
  // the rest of user administration.
  //
  // What it does *not* change: `u.view` keeps lifting the project gate on
  // reading a thread by id, so no installation loses a read on upgrade. The two
  // grants overlap there and only there. What neither of them does any more is
  // decide what arrives unasked — the sidebar and the shell stream are
  // membership for every account now, admins included, which is what makes a
  // deliberate "go and look" screen necessary in the first place.
  //
  // Read-only, and it stays that way: opening a thread from the directory grants
  // nothing inside it. Every command still goes through the project's own
  // `p.thread.*` grants.
  //
  // Nor does it make its holder an administrator. It lifts the project gate,
  // because a directory that spans the installation has to name projects its
  // reader does not belong to — but `ThreadVisibility.administersInstallation`
  // stays false, and the screens that ask for membership rather than for sight
  // keep refusing: Settings → Worktrees still shows this reader their own
  // working directories, not everybody's paths and branch names.
  "threads.view_all",

  // Whether this person gets a thread recap — the one-line "where this stands"
  // that a settled turn writes for whoever was away.
  //
  // A permission rather than a preference table, and granted rather than
  // withheld, because of what it costs. Every recap is a `codex exec` of its
  // own, and the reactor pays as soon as *one* member of a thread's audience is
  // owed one. So this is not "may I read a sentence" — it is "may the server
  // spend on my behalf", which is the same kind of question as `p.thread.use`
  // and belongs in the same place: an administrator's hands, off until someone
  // decides otherwise.
  //
  // Two consequences worth knowing before granting it widely. The recap is per
  // *thread*, not per person, so one holder on a thread pays for it and the
  // saving only appears when nobody on that thread holds it. And withholding it
  // hides nothing: the text summarises a conversation the person can already
  // open in full, so the chat simply stops showing them the row.
  "threads.recap",
] as const;

export const GlobalPermission = Schema.Literals(GLOBAL_PERMISSIONS);
export type GlobalPermission = typeof GlobalPermission.Type;

export const PROJECT_PERMISSIONS = [
  // Project itself
  "p.edit",
  "p.delete",

  // Threads (chat with the AI agent)
  //
  // `p.thread.handoff` is carved out of `p.thread.create` because a handoff is
  // not an ordinary new thread: it copies the conversation into a *second*
  // provider and continues it there. That is a decision about which vendor sees
  // the work and which budget pays for it, so an admin has to be able to pin an
  // account to one provider without also forbidding it from opening threads.
  // It is required *in addition to* `p.thread.create` — a handoff still creates
  // a thread, and nobody should get one through a side door. Migration 035
  // grants it to everyone who held `p.thread.create`, which is exactly who
  // could hand off yesterday.
  "p.thread.create",
  "p.thread.handoff",
  "p.thread.use",
  "p.thread.delete",

  // Terminal
  "p.terminal",

  // Git
  //
  // Three grants, split by what the action does to other people's work rather
  // than by which command it runs:
  //  - `p.git_read` reads GitHub (pull request feedback, the issue backlog).
  //  - `p.git_sync` brings code *in*: fetch, pull, fetch a PR's head, and merge
  //    another ref into this workspace's branch. Nothing leaves, and nothing
  //    outside the workspace changes.
  //  - `p.git_write` sends code *out*, or writes history: commit, push, open a
  //    PR. It is also what shows the git actions in the chat header.
  //
  // The split exists because "let the agent pull in a colleague's PR to try it"
  // and "let this account push" are different decisions, and a PM needs the
  // first without the second. Migration 034 grants `p.git_sync` to everyone who
  // held `p.git_write`, which is exactly who could already do these four.
  "p.git_read",
  "p.git_sync",
  "p.git_write",

  // The Pull Requests section: the listing across projects, a pull request's
  // detail, its timeline and its diff.
  //
  // Carved out of `p.git_read` rather than riding on it, because the two answer
  // different questions. `p.git_read` is what an *agent* needs to read review
  // feedback while it works, and it is granted freely for that; the section is a
  // standing window onto everything happening in a repository, which is a
  // decision about a *person*. Without the split, hiding the section from an
  // account also blinds its agents.
  //
  // It gates reading only. Acting on a pull request — merge, close, reopen,
  // ready-for-review, comment — needs this *and* `p.git_write`: the first says
  // the account may be in the section at all, the second that it may change
  // something there. Requiring only the second would leave the actions reachable
  // over the websocket for an account that cannot see the section.
  //
  // Migration 046 grants it wherever `p.git_read` is already held, which is
  // exactly who could open the section yesterday.
  "p.pull_requests.view",

  // Project actions (the "Actions" control in the chat header).
  //
  // Two grants, split by the question an admin actually asks: may this account
  // *run* the project's vetted commands, and may it *change what they are*.
  // They used to be answered by two unrelated permissions — running rode on
  // `p.thread.use` and authoring on `p.edit` — which meant "let the PM run the
  // seeder but not invent commands" was unsayable, and the Add/Edit affordances
  // showed to everyone regardless because the control asked nobody.
  //
  //  - `p.action.run` GATES `workspace.runProjectScript`, the background
  //    runner. For the other two kinds it is an affordance and the real gate
  //    sits underneath: a `terminal` action still needs `p.terminal`, and a
  //    `chat` action sends a prompt the user could have typed. Withholding it
  //    hides the whole control.
  //  - `p.action.create` GATES `project.meta.update` when the payload carries
  //    `scripts`, which is how every create, edit and delete of an action
  //    travels. Required *in addition to* `p.edit`, so it narrows who may
  //    author actions without widening who may edit the project.
  //
  // Automatic triggers (`onWorktreeCreate`, `onThreadArchive`) run server-side
  // with no user in scope and are deliberately outside both: they are the
  // project's own configuration, not somebody's click.
  "p.action.run",
  "p.action.create",

  // Running whitelisted commands on the *user's own machine*, through the
  // desktop app (the `taskin_local` MCP family). Split the same way project
  // actions are, and for the same reason: running a vetted command and deciding
  // what the vetted commands are is not the same decision.
  //
  //  - `p.local.exec` GATES every `taskin_local` tool call, re-checked per call
  //    against the thread's author (`agentLocalExecTools.ts`). Without it the
  //    tools deny, whatever the agent's tool allowlist says.
  //  - `p.local.manage` GATES `project.meta.update` when the payload carries
  //    `localActions`, which is how every create, edit and delete of a local
  //    command travels. Required *in addition to* `p.edit`, mirroring
  //    `p.action.create`.
  //
  // Migration 039 keys them on the action pair — `p.local.exec` on
  // `p.action.run`, `p.local.manage` on `p.action.create` — so whoever already
  // decides which vetted commands exist keeps deciding. Granting them changes
  // nothing on its own: the catalog starts empty, and each machine still has to
  // accept an entry before anything of it can run there.
  "p.local.exec",
  "p.local.manage",

  // Start/stop dev server for a thread and obtain the preview URL. It is the
  // only permission behind that control: the preview button sits in the branch
  // toolbar for layout reasons, but `p.branch.view` does not govern it, and the
  // toolbar renders for either grant so withholding one never hides the other.
  "p.runtime.expose",

  // Drop the login in front of a preview so anything on the internet can reach
  // it — a mobile client, a partner's front-end, a webhook sender. Deliberately
  // NOT folded into `p.runtime.expose`: starting a dev server publishes it to
  // people who already have an account, while this publishes an unhardened
  // process, running arbitrary repository code, to everyone. The grant it
  // creates always expires (see `DevServerStatus.publicUntil`), and no
  // migration seeds it — an admin hands it out on purpose.
  "p.runtime.expose_public",

  // Write to the project's linked Plane board from a thread: comment on an
  // issue, move its state, self-assign. Reading the board needs no permission
  // beyond being able to use the thread, matching how `taskin_preview` reads
  // status ungated while `taskin_preview_control` gates the mutations.
  "p.plane.write",

  // ── The non-technical profile ─────────────────────────────────────────
  //
  // Five grants that exist to be *withheld*. A PM working in a persistent
  // environment needs a chat, a preview and their data; a diff panel, a plan
  // sidebar and a model picker are noise at best and a way to break the
  // environment at worst. Every one of them is granted to everyone who can use
  // a thread by migration 030, so nobody loses anything on deploy — they only
  // matter once an admin takes one away.
  //
  // Two of them are real gates, and three are affordances. Saying which is
  // which here is the point: an affordance that is documented as a security
  // boundary is how a boundary gets trusted that was never there.
  //
  //  - `p.env.use` GATES: creating a thread that borrows a persistent
  //    environment (`commandAuthorization.ts`). Not seeded by 030 — nobody had
  //    an environment before it existed, so there is nothing to preserve, and
  //    an admin who provisions one grants this deliberately.
  //  - `p.env.code_update` GATES: `git_checkout`, `git_merge` and
  //    `git_reset_to_base`, the three tools that move an environment's code to
  //    another ref (`agentGitTools.ts`). Split from `p.git_write` because they
  //    are a different power: committing changes the work inside a workspace,
  //    while these change *which code the workspace is*, for every thread
  //    sharing it at once. A PM needs the first to get anything done and has no
  //    business with the second — asking for a feature should not be one
  //    sentence away from moving the environment onto someone else's branch.
  //  - `p.model.select` GATES: the server ignores a `modelSelection` sent by a
  //    holder-less client and resolves the default instead
  //    (`modelResolution.ts`), so a pinned account stays on its agent.
  //  - `p.diff.view` GATES: the two diff queries in `wsServer.ts`.
  //  - `p.files.view` GATES: browsing the workspace's files at all —
  //    `projects.listDirectory`, `projects.readFile` and
  //    `projects.searchEntries` in `wsServer.ts`. All three and not just the
  //    first two, because the composer's `@` picker and the sidebar palette
  //    read the same index: hiding the panel while leaving the search would be
  //    a boundary that enumerates everything it claims to hide. The cost is
  //    that withholding it also takes away `@`-mentioning a file, which is the
  //    right trade for the profile this exists for and a surprise for anyone
  //    else — so it is granted to every thread user by migration 033 and only
  //    matters once an admin takes it away.
  //  - `p.plan.view` and `p.branch.view` are UI ONLY. `p.branch.view` covers
  //    the branch selector, the workspace picker, the approvals toggle and the
  //    usage meter — everything in that row *except* the preview, which has its
  //    own grant above. Both ride inside the
  //    read model the thread already needs, so withholding them hides the
  //    panel without hiding the data — enough to simplify a screen, not enough
  //    to keep a secret.
  //  - `p.env.manage` GATES: creating and destroying the *project's* own
  //    environments — the shared QA and staging workspaces that belong to
  //    nobody. Separate from `p.env.use` because the two are opposite ends of
  //    the same object: everybody on the project may work inside `qa`, and
  //    almost nobody should be able to make a second one or take it away from
  //    the people using it. Seeded (059) to whoever already holds the global
  //    `u.assign_permissions`, who could grant it to themselves anyway.
  //  - `p.env.sync` GATES: moving a project environment onto its base branch
  //    and the data operations that go with it. Split from `p.env.code_update`
  //    for the same reason that one was split from `p.git_write`: "bring QA up
  //    to date" is a routine act a PM performs through an agent, while
  //    "checkout an arbitrary ref here" is not.
  //  - `p.pr.preview` GATES: trying a pull request on a project environment —
  //    `qa_try_pr`, `qa_back_to_base`, and the "Probar sobre QA" button in the
  //    Pull Requests section. Split from `p.env.sync` because the two differ in
  //    *whose* code lands in a shared workspace: a sync brings in the branch the
  //    project already agreed on, while a trial merges an unreviewed pull
  //    request — including its migrations — into the environment several people
  //    are using. Seeded (062) from `p.git_sync`, which is exactly the
  //    population that could already fetch and merge somebody's PR head.
  "p.env.use",
  "p.env.code_update",
  "p.env.manage",
  "p.env.sync",
  "p.pr.preview",
  "p.diff.view",
  "p.files.view",
  "p.plan.view",
  "p.model.select",
  "p.branch.view",
] as const;

export const ProjectPermission = Schema.Literals(PROJECT_PERMISSIONS);
export type ProjectPermission = typeof ProjectPermission.Type;

export const Permission = Schema.Union([GlobalPermission, ProjectPermission]);
export type Permission = typeof Permission.Type;

export const ProjectAssignment = Schema.Struct({
  projectId: ProjectId,
  permissions: Schema.Array(ProjectPermission),
});
export type ProjectAssignment = typeof ProjectAssignment.Type;

// Per-user model policy. The allowlist enumerates (provider, slug) pairs the
// user may dispatch; defaultModel is applied when neither the command nor the
// project specifies one. Both are sent verbatim by /auth/me and /auth/login so
// the client can hide UI surfaces for forbidden models.
export const AllowedModel = Schema.Struct({
  provider: ProviderKind,
  model: Schema.String,
});
export type AllowedModel = typeof AllowedModel.Type;

/**
 * Per-user managed agent policy.
 *
 * Deliberately *not* symmetric with the model allowlist. Models are fail-closed:
 * an empty allowlist means the user may dispatch nothing. Agents already had a
 * visibility rule before this existed — team grants, plus "an agent on no team
 * is visible to everyone" — and flipping that to fail-closed would have taken
 * every agent away from every user on deploy.
 *
 * So the rule is: an empty list means "no personal policy, fall back to the team
 * rule", and a non-empty list is *exclusive* — it replaces team grants and house
 * agents both. That is what makes it possible to pin a non-technical user to one
 * agent without touching how anyone else's account behaves.
 *
 * `defaultAgentId` is the counterpart of `defaultModel`: applied when neither the
 * command nor the project names one. It carries no provider/model of its own —
 * those are read from the agent, so an agent that is retargeted at a new model
 * moves its users with it.
 */
export const AuthenticatedUser = Schema.Struct({
  id: UserId,
  email: Schema.String,
  name: Schema.String,
  roleLabel: Schema.NullOr(Schema.String),
  permissions: Schema.Array(GlobalPermission),
  projectAssignments: Schema.Array(ProjectAssignment),
  allowedModels: Schema.Array(AllowedModel),
  defaultModel: Schema.NullOr(AllowedModel),
  allowedAgentIds: Schema.Array(ManagedAgentId),
  defaultAgentId: Schema.NullOr(ManagedAgentId),
  mustChangePassword: Schema.Boolean,
  createdAt: IsoDateTime,
  avatarFileName: Schema.NullOr(Schema.String),
});
export type AuthenticatedUser = typeof AuthenticatedUser.Type;

/**
 * Minimal user listing every authenticated user may read.
 *
 * Sharing a thread requires knowing who else exists, but that must not turn
 * into a back door onto the admin user list: the directory carries an id, a
 * display label and the avatar file name — nothing else, no email,
 * permissions, assignments or password state. The avatar is display metadata
 * equivalent to displayName: whoever may see a name may see the picture next
 * to it.
 */
export const UserDirectoryEntry = Schema.Struct({
  id: UserId,
  displayName: Schema.String,
  avatarFileName: Schema.NullOr(Schema.String),
});
export type UserDirectoryEntry = typeof UserDirectoryEntry.Type;

export const UserDirectoryResponse = Schema.Struct({
  users: Schema.Array(UserDirectoryEntry),
});
export type UserDirectoryResponse = typeof UserDirectoryResponse.Type;

/**
 * Teams — the grouping that decides which users may see and assign each other.
 *
 * Read by the admin Teams screen (`u.view` to list, `u.edit` to change) and,
 * indirectly, by every user through the directory: two accounts are mutually
 * visible when they share at least one live team. Membership is carried as a
 * flat list of ids rather than embedded user objects so the same payload can
 * serve an admin without leaking anything the directory does not already
 * expose.
 */
export const Team = Schema.Struct({
  id: TeamId,
  name: Schema.String,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Team = typeof Team.Type;

export const TeamWithMembers = Schema.Struct({
  ...Team.fields,
  memberIds: Schema.Array(UserId),
});
export type TeamWithMembers = typeof TeamWithMembers.Type;

export const AdminTeamsResponse = Schema.Struct({
  teams: Schema.Array(TeamWithMembers),
});
export type AdminTeamsResponse = typeof AdminTeamsResponse.Type;

/** Single-team envelope returned by create, rename and membership edits. */
export const AdminTeamResponse = Schema.Struct({
  team: TeamWithMembers,
});
export type AdminTeamResponse = typeof AdminTeamResponse.Type;

export const TEAM_NAME_MAX_LENGTH = 80;

export const LoginRequest = Schema.Struct({
  email: Schema.String,
  password: Schema.String,
});
export type LoginRequest = typeof LoginRequest.Type;

export const LoginResponse = Schema.Struct({
  user: AuthenticatedUser,
});
export type LoginResponse = typeof LoginResponse.Type;

export const SESSION_COOKIE_NAME = "taskin_session";
export const SESSION_TTL_SECONDS = 60 * 60 * 24 * 7;
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Ceilings for the two identity fields a form can edit.
 *
 * Shared for the same reason as `AVATAR_MAX_BYTES`: the input caps what can be
 * typed and the server refuses what arrives anyway, and two copies of the
 * number would drift into a field that accepts what the save then rejects. The
 * email bound is the RFC 5321 maximum for an address; the name bound matches
 * `TEAM_NAME_MAX_LENGTH` because both are display labels in the same UI.
 */
export const USER_NAME_MAX_LENGTH = 80;
export const USER_EMAIL_MAX_LENGTH = 254;

/**
 * Ceiling for a profile image, in decoded bytes.
 *
 * Shared rather than restated on each side, like `PASSWORD_MIN_LENGTH`: the
 * client rejects an oversized file before spending a base64 encode and an
 * upload on it, and the server rejects it because a client is not a security
 * boundary. Two copies of the number would eventually disagree, and the way
 * that fails is a picker that accepts a file the upload then refuses.
 */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/**
 * What a profile image may be. SVG is absent on purpose — it is a document
 * that executes, not a picture — and the server does not take this list on
 * faith: it sniffs the bytes. This is what the file picker filters on and what
 * the error message names.
 */
export const AVATAR_ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;
